import { ErrorCode } from '@ps/web-core-enums';
import { DebugKey, canUseDOM, createDebug, tryParse } from '@ps/web-core-utils';
import { isObject, snakeCase } from 'lodash';
import type { Histogram } from 'prom-client';
import { defer, firstValueFrom } from 'rxjs';
import { filter, first, switchMap, tap, timeout } from 'rxjs/operators';
import URL from 'url-parse';
import type { Required } from 'utility-types';

import { RequestError } from './RequestError';
import type { connect } from './connect';
import { ConnectionState } from './enums/ConnectionState';
import { StatusCode } from './enums/StatusCode';
import { WMTDisconnectReason } from './enums/WMTDisconnectReason';
import { subscribe } from './subscribe';
import type { HttpHeaders, HttpMethods, Message, StompHeaders } from './types';
import { handleError } from './utils/handleError';
import type { ChallengeBody } from './utils/performPow';
import { performPow } from './utils/performPow';

export interface RequestOptions<TBody = unknown> {
  body?: TBody;
  connection: ReturnType<typeof connect>;
  headers?: HttpHeaders;
  http?: boolean;
  httpDomain?: string;
  method?: HttpMethods;
  service: string;
  timeout?: number;
  silent?: boolean;
  signal?: AbortSignal;
  retry?: { count: number; delay?: (retryCount: number) => number };
}

const versionRegExp = /^\/api\/v(\d)/;
const errorCase = (name: string) => snakeCase(name).toUpperCase();
let histogram: Histogram | undefined;
let messageId = 0;

/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle */
declare global {
  var __PS_REQUEST_HISTOGRAM: Histogram;
}

if (process.env.NEXT_RUNTIME === 'nodejs') {
  histogram =
    global.__PS_REQUEST_HISTOGRAM ||
    new (require('prom-client').Histogram)({
      help: 'API request duration in milliseconds.',
      name: 'api_requests',
      buckets: [50, 100, 200, 300, 400, 500, 1000, 2000],
      labelNames: ['service']
    });
  global.__PS_REQUEST_HISTOGRAM = histogram;
}
/* eslint-enable */

const success = (startTime: number, service: string, body: unknown, requestId?: string) => {
  const debug = createDebug(DebugKey.Request, requestId);
  const info = createDebug(DebugKey.RequestInfo, requestId);
  const duration = Date.now() - startTime;
  debug('success %s [%dms]', service, duration);
  info('success %s %o', service, body);

  if (process.env.CLIENT_FRAMEWORK !== 'NEXTJS') {
    histogram?.observe({ service }, duration);
  }
};

const httpRequest = <TMessage extends Message<unknown>>(
  url: string,
  init: Omit<RequestInit, 'method' | 'headers'> & { method: HttpMethods; headers: HttpHeaders },
  service: string,
  startTime: number
): Promise<TMessage> => {
  const requestId = init.headers['x-request-id'];

  return fetch(url, init as RequestInit)
    .catch((err) => {
      throw new RequestError({
        service,
        method: init.method,
        body: { code: errorCase(err.name), text: err.message },
        requestId,
        cause: err
      });
    })
    .then(async (response: Response) => {
      const text = await response.text();
      let body;

      try {
        if (text) body = JSON.parse(text) as { code: string; challenge: ChallengeBody };
      } catch (err) {
        const { type, name, message } = err as RequestError & { type?: string };
        throw new RequestError({
          service,
          method: init.method,
          body: { code: errorCase(type || name), text: message },
          response,
          requestId,
          cause: err
        });
      }

      if (response.ok) {
        success(startTime, service, body, requestId);
        return { headers: Object.fromEntries(response.headers.entries()), body } as TMessage;
      } else if (body?.code === ErrorCode.POW_REQUIRED && !init.headers['pow-result']) {
        init.headers['pow-result'] = performPow(body.challenge);
        return httpRequest(url, init, service, startTime);
      }

      throw new RequestError({ service, body, response, method: init.method, requestId });
    });
};

export async function request<TBody = unknown, TCustomHeader extends string | void = void>({
  service,
  body,
  method = 'GET',
  connection,
  http,
  httpDomain,
  timeout: timeoutOverride,
  silent,
  signal,
  retry = { count: 0 },
  ...rest
}: RequestOptions) {
  const headers: StompHeaders = rest.headers || {};
  const requestId = headers['x-request-id'];
  const debug = createDebug(DebugKey.Request, requestId);
  const id = messageId++;
  const startTime = Date.now();
  const requestBody = JSON.stringify(body);
  const [, version = 0] = service.match(versionRegExp)?.map((item) => parseInt(item)) || [];

  body ? debug('start %s %o', service, body) : debug('start %s', service);
  headers.accept = headers.accept || 'application/json';

  if (http) {
    const url = new URL(service, httpDomain);
    let httpBody: undefined | BodyInit;

    if (canUseDOM) {
      delete headers.referer;
    }

    if (['GET', 'HEAD'].includes(method) && isObject(body)) {
      let queryResult = '';
      Object.entries(body).forEach(([key, value]) => {
        const paramValue = typeof value !== 'string' ? JSON.stringify(value) : value;
        if (queryResult.length) {
          queryResult += '&';
        }
        queryResult += key + '=' + paramValue;
      });
      url.set('query', queryResult);
      httpBody = undefined;
    } else {
      headers['content-type'] = 'application/json';
      httpBody = requestBody;
    }

    if ((body instanceof URLSearchParams || body instanceof FormData) && method === 'POST') {
      delete headers['content-type'];
      httpBody = body;
    }

    return firstValueFrom(
      defer(() =>
        httpRequest<Message<TBody, TCustomHeader>>(
          url.toString(),
          { method, headers, body: httpBody, signal, credentials: 'include' },
          service,
          startTime
        )
      ).pipe(handleError({ retry, method, service, requestId }))
    );
  }

  headers['message-id'] = id.toString();
  headers['content-type'] = 'application/json';
  if (version === 0) {
    headers.type = service;
  } else {
    const [, , , ...rest] = service.split('/');
    headers.destination = `/${rest.join('/')}`;
    headers['x-sw-version'] = version.toString();
    headers.method = method;
  }

  return firstValueFrom(
    connection.pipe(
      tap(({ client, state, config, disconnectReason }) => {
        switch (true) {
          case disconnectReason === WMTDisconnectReason.InvalidToken:
            delete headers.token;
            delete headers.signature;
            delete headers.authorization;
            break;
          case state === ConnectionState.CLOSED:
            throw new RequestError({
              service,
              method,
              body: {
                code: ErrorCode.CONNECTION_CLOSED,
                text: 'The connection was closed before a response was received.'
              },
              requestId
            });
          case state === ConnectionState.CONNECTED:
            client?.send(headers.destination || config.requestDestination, headers, requestBody);
            break;
        }
      }),
      filter(({ state }) => state === ConnectionState.CONNECTED),
      switchMap(({ client, config }) => {
        const { requestDestination, requestTimeout } = config;
        const actualTimeout = timeoutOverride || requestTimeout;
        const responseChannel = version > 0 ? '/user/queue/response' : config.responseChannel;

        return subscribe<
          Message<TBody, TCustomHeader, Required<StompHeaders, 'status-code' | 'reply-id'>>
        >(responseChannel, {}, connection).pipe(
          first(({ headers, body }) => {
            const { 'status-code': statusCode, 'reply-id': replyId } = headers;
            if (parseInt(replyId) !== id) return false;

            if (version > 0) {
              const status = parseInt(statusCode);

              const Blob = canUseDOM ? window.Blob : require('buffer').Blob;
              if (status >= 400) {
                throw new RequestError({
                  service,
                  body,
                  response: new Response(new Blob(), { status }),
                  method,
                  requestId
                });
              }
              // TODO v1 POW handling
              success(startTime, service, body, requestId);
              return true;
            }

            switch (tryParse(statusCode)) {
              case StatusCode.POW_REQUIRED:
                headers['pow-result'] = performPow(body as ChallengeBody);
                client?.send(requestDestination, headers, requestBody);
                return false;
              case StatusCode.OK:
                success(startTime, service, body, requestId);
                return true;
              default:
                throw new RequestError({ headers, body, service, method, requestId });
            }
          }),
          timeout(actualTimeout)
        );
      }),
      handleError({ retry, method, service, requestId }),
      tap({
        error: (error: RequestError | Error) => {
          // eslint-disable-next-line no-console
          if (!silent) error instanceof RequestError ? error.log() : console.error(error);
        }
      }),
      first()
    )
  );
}
